import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Activity, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ActivityTypes() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.get("/activity-types").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setSaving(true);
    try {
      await api.post("/activity-types", { name: n });
      setName("");
      load();
      toast.success("Attività aggiunta");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/activity-types/${editing.id}`, { name: editName.trim() });
      setEditing(null);
      load();
      toast.success("Attività aggiornata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/activity-types/${delTarget.id}`);
      setDelTarget(null);
      load();
      toast.success("Attività eliminata");
    } catch (e) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Tipi di attività</h1>
        <p className="mt-1 text-sm text-slate-500">
          Definisci i profili di attività da assegnare ai dipendenti. Dispositivi e attrezzature possono essere associati a queste attività per filtrare il check-in.
        </p>
      </div>
      <Card className="rounded-lg border-slate-200 p-5 shadow-sm">
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Es. Elettricista, Installatore FTTH, Manutentore..." data-testid="activity-type-input" />
          <Button onClick={add} disabled={saving || !name.trim()} data-testid="activity-type-add-btn" className="shrink-0 rounded-md bg-[#002FA7] text-white hover:bg-[#00227A]"><Plus className="mr-1.5 h-4 w-4" />Aggiungi</Button>
        </div>
        <div className="mt-4 space-y-1.5" data-testid="activity-types-list">
          {items.length === 0 ? (
            <p className="text-sm text-slate-400">Nessun tipo di attività configurato.</p>
          ) : items.map((it) => (
            <div key={it.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2" data-testid={`activity-type-row-${it.id}`}>
              <span className="flex items-center gap-2 text-sm text-slate-800"><Activity className="h-4 w-4 text-[#002FA7]" />{it.name}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => { setEditing(it); setEditName(it.name); }} data-testid={`activity-type-edit-${it.id}`} className="text-slate-400 hover:text-[#002FA7]"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setDelTarget(it)} data-testid={`activity-type-del-${it.id}`} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="activity-type-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifica attività</DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="at-edit-name">Nome</Label>
            <Input id="at-edit-name" value={editName} onChange={(e) => setEditName(e.target.value)} className="mt-1.5" data-testid="activity-type-edit-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button onClick={saveEdit} disabled={!editName.trim()} data-testid="activity-type-save-btn" className="bg-[#002FA7] text-white hover:bg-[#00227A]">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="activity-type-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminare "{delTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              L'attività verrà rimossa dai dipendenti e dalle attrezzature a cui è associata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="activity-type-confirm-delete-btn" className="bg-rose-600 text-white hover:bg-rose-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
